'use client';

import React from 'react';
import { cn } from '@/lib/utils';

export type DashboardBadgeVariant = 'cyan' | 'magenta' | 'purple' | 'green' | 'amber' | 'default';

export interface DashboardBadgeProps {
  children: React.ReactNode;
  variant?: DashboardBadgeVariant;
  className?: string;
  removable?: boolean;
  onRemove?: () => void;
}

const variantClasses: Record<DashboardBadgeVariant, string> = {
  cyan: 'bg-neon-cyan/10 text-neon-cyan border-neon-cyan/25',
  magenta: 'bg-neon-magenta/10 text-neon-magenta border-neon-magenta/25',
  purple: 'bg-neon-purple/10 text-neon-purple border-neon-purple/25',
  green: 'bg-neon-green/10 text-neon-green border-neon-green/25',
  amber: 'bg-neon-amber/10 text-neon-amber border-neon-amber/25',
  default: 'bg-panel text-foreground border-neon-cyan/15',
};

const DashboardBadge: React.FC<DashboardBadgeProps> = ({
  children,
  variant = 'cyan',
  className,
  removable = false,
  onRemove,
}) => {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium',
        variantClasses[variant],
        className
      )}
    >
      {children}
      {removable && onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="ml-0.5 opacity-60 transition-opacity hover:opacity-100"
          aria-label={`Remove ${children}`}
        >
          <svg
            className="h-3 w-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
    </span>
  );
};

export default DashboardBadge;
